import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const setCookie = (name, value, days = 30) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

export const saveUserPreference = (key, value) => setCookie(`pref_${key}`, value, 365);
export const getUserPreference  = (key)        => getCookie(`pref_${key}`);

const ALLOWED_PATHS = ["/privacy", "/terms", "/login", "/register"];

const CookieBanner = () => {
  const [showBanner, setShowBanner]   = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [agreed, setAgreed]           = useState(false);
  const [preferences, setPreferences] = useState({
    essential:   true,
    session:     true,
    preferences: true,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const isAllowedPath = (path) =>
    ALLOWED_PATHS.includes(path) || path.startsWith("/admin");

  useEffect(() => {
    const consent = getCookie("cookie_consent");

    if (!consent) {
      if (isAllowedPath(location.pathname)) {
        // On legal/auth pages — no banner, no block
        setShowBanner(false);
        document.body.style.overflow = "";
      } else {
        // On any other page — show banner and block
        setShowBanner(true);
        document.body.style.overflow = "hidden";
      }
    } else if (consent === "accepted") {
      startSessionTracking();
      setShowBanner(false);
      document.body.style.overflow = "";
    } else {
      // declined
      setShowBanner(false);
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [location.pathname]);

  // Block navigation away from allowed paths when no consent
  useEffect(() => {
    const consent = getCookie("cookie_consent");
    if (!consent && !isAllowedPath(location.pathname)) {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  const startSessionTracking = () => {
    if (!getCookie("session_id")) {
      setCookie("session_id", `session_${Date.now()}`, 1);
    }
    setCookie("last_visit", new Date().toISOString(), 30);
  };

  const handleAccept = () => {
    if (!agreed) return;
    setCookie("cookie_consent", "accepted", 365);
    setCookie("cookie_preferences", JSON.stringify(preferences), 365);
    startSessionTracking();
    document.body.style.overflow = "";
    setShowBanner(false);
  };

  const handleDecline = () => {
    setCookie("cookie_consent", "declined", 365);
    deleteCookie("session_id");
    deleteCookie("last_visit");
    document.body.style.overflow = "";
    setShowBanner(false);
  };

  const togglePref = (key) => {
    if (key === "essential") return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(60px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .cookie-toggle {
          position: relative;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .cookie-toggle input { opacity: 0; width: 0; height: 0; }
        .cookie-toggle-slider {
          position: absolute;
          inset: 0;
          background: #444;
          border-radius: 22px;
          cursor: pointer;
          transition: 0.3s;
        }
        .cookie-toggle-slider::before {
          content: "";
          position: absolute;
          width: 16px;
          height: 16px;
          left: 3px;
          bottom: 3px;
          background: #fff;
          border-radius: 50%;
          transition: 0.3s;
        }
        .cookie-toggle input:checked + .cookie-toggle-slider { background: #0a9b7c; }
        .cookie-toggle input:checked + .cookie-toggle-slider::before { transform: translateX(18px); }
        .cookie-toggle input:disabled + .cookie-toggle-slider { opacity: 0.6; cursor: not-allowed; }
        .cookie-checkbox {
          width: 18px;
          height: 18px;
          accent-color: #0a9b7c;
          cursor: pointer;
          flex-shrink: 0;
        }
        .cookie-accept-btn {
          padding: 12px 28px;
          background: #0a9b7c;
          border: none;
          color: #fff;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 700;
          font-size: 14px;
          transition: background 0.2s, opacity 0.2s;
        }
        .cookie-accept-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .cookie-accept-btn:not(:disabled):hover { background: #088a6d; }
        .cookie-decline-btn {
          padding: 12px 20px;
          background: transparent;
          border: 1px solid #555;
          color: #aaa;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          transition: border-color 0.2s, color 0.2s;
        }
        .cookie-decline-btn:hover { border-color: #888; color: #ddd; }
        .cookie-link {
          color: #0a9b7c;
          text-decoration: underline;
          cursor: pointer;
        }
        .cookie-link:hover { color: #0cbb96; }
      `}</style>

      <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        zIndex: 99998,
        animation: "fadeIn 0.3s ease",
        backdropFilter: "blur(4px)",
      }} />

      <div style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        animation: "slideUp 0.4s ease",
        maxHeight: "90vh",
        overflowY: "auto",
      }}>
        <div style={{
          background: "#0d1f1b",
          borderTop: "2px solid #0a9b7c",
          padding: "32px",
          maxWidth: "860px",
          margin: "0 auto",
          borderRadius: "16px 16px 0 0",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
          color: "#fff",
          fontFamily: "sans-serif",
        }}>

          <div style={{ marginBottom: "16px" }}>
            <p style={{ margin: "0 0 4px 0", fontSize: "20px", fontWeight: "700" }}>
              🍪 Cookie & Privacy Notice
            </p>
            <p style={{ margin: 0, fontSize: "12px", color: "#0a9b7c", fontWeight: 600 }}>
              In accordance with the Kenya Data Protection Act, 2019
            </p>
          </div>

          <p style={{ fontSize: "13px", color: "#ccc", lineHeight: "1.7", marginBottom: "16px" }}>
            Solviotech uses cookies and similar technologies to provide, improve, and secure our services.
            Under the <strong style={{ color: "#fff" }}>Kenya Data Protection Act, 2019</strong>, we are
            required to inform you of how we collect and use your data, and to obtain your explicit consent
            before proceeding. Please review our{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="cookie-link">Privacy Policy</a>
            {" "}and{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="cookie-link">Terms of Service</a>
            {" "}before continuing.
          </p>

          <div style={{ marginBottom: "20px" }}>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={{
                background: "none", border: "none", color: "#0a9b7c",
                cursor: "pointer", fontSize: "13px", padding: 0,
                textDecoration: "underline", marginBottom: "12px"
              }}
            >
              {showDetails ? "▲ Hide cookie details" : "▼ Show cookie details"}
            </button>

            {showDetails && (
              <div style={{
                background: "#0a1a16",
                borderRadius: "10px",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
              }}>
                {[
                  { key: "essential",   icon: "🔒", title: "Essential Cookies",   desc: "Required for the site to function. Cannot be disabled.",   duration: "Session" },
                  { key: "session",     icon: "📊", title: "Session Cookies",     desc: "Track your visit session to maintain state across pages.", duration: "1 day"   },
                  { key: "preferences", icon: "⚙️", title: "Preference Cookies", desc: "Remember your language, theme, and other settings.",       duration: "1 year"  },
                ].map((item) => (
                  <div key={item.key} style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                  }}>
                    <div>
                      <p style={{ margin: "0 0 2px 0", fontSize: "13px", fontWeight: 600 }}>
                        {item.icon} {item.title}
                        <span style={{ marginLeft: "8px", fontSize: "11px", color: "#0a9b7c", background: "#0a9b7c22", padding: "2px 6px", borderRadius: "4px" }}>
                          {item.duration}
                        </span>
                      </p>
                      <p style={{ margin: 0, fontSize: "12px", color: "#888" }}>{item.desc}</p>
                    </div>
                    <label className="cookie-toggle">
                      <input
                        type="checkbox"
                        checked={preferences[item.key]}
                        disabled={item.key === "essential"}
                        onChange={() => togglePref(item.key)}
                      />
                      <span className="cookie-toggle-slider"></span>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            fontSize: "13px",
            color: "#ccc",
            cursor: "pointer",
            marginBottom: "20px",
            lineHeight: "1.6",
          }}>
            <input
              type="checkbox"
              className="cookie-checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ marginTop: "2px" }}
            />
            <span>
              I have read and agree to the{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="cookie-link">Privacy Policy</a>
              {" "}and{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="cookie-link">Terms of Service</a>.
              {" "}I understand how Solviotech collects and uses my data in accordance with the
              Kenya Data Protection Act, 2019.
            </span>
          </label>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button className="cookie-accept-btn" onClick={handleAccept} disabled={!agreed}>
              ✓ Accept & Continue
            </button>
            <button className="cookie-decline-btn" onClick={handleDecline}>
              Decline
            </button>
          </div>

          <p style={{ margin: "16px 0 0 0", fontSize: "11px", color: "#555", lineHeight: "1.6" }}>
            You can withdraw your consent at any time by clearing your browser cookies.
            Declining cookies may limit some features of the site.
            For queries, contact us at{" "}
            <a href="mailto:info@solviotech.com" className="cookie-link">info@solviotech.com</a>.
          </p>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;